import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, interval, Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { SystemService } from './system.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private subscriptionsCache: { [key: string]: any } = {}; // Store cache per filter key
  private subscriptionStatus = new BehaviorSubject<boolean>(true);
  subscriptionStatus$ = this.subscriptionStatus.asObservable();
  private lastCheckedStatus: boolean = true; // ✅ Store last known status
  private currentRoute: string = ''; // ✅ Track current route

  constructor(private http: HttpClient, private system: SystemService, private router: Router) {
    this.trackCurrentRoute();
    this.startAutoCheck(); // ✅ Start automatic subscription check
  }

  // ✅ Track the current route to prevent redirect loops
  private trackCurrentRoute() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects;
      }
    });
  }

  // ✅ Check subscription manually (called at app start)
  checkSubscription() {
    this.http.get<{ active: boolean, expiry_date: string }>(`${this.system.apiURL}/subscription/status`)
      .subscribe(response => {
        this.handleSubscriptionChange(response.active);
      });
  }

  // ✅ Auto-check subscription every 10 minutes
  startAutoCheck() {
    interval(600000) // ✅ 10 minutes
      .pipe(switchMap(() => this.http.get<{ active: boolean, expiry_date: string }>(`${this.system.apiURL}/subscription/status`)))
      .subscribe(response => {
        this.handleSubscriptionChange(response.active);
      });
  }

  // ✅ Handle subscription status change
  private handleSubscriptionChange(active: boolean) {
    this.subscriptionStatus.next(active);

    if (!active && this.lastCheckedStatus) {
      this.lastCheckedStatus = false; // ✅ Prevent repeated redirects

      if (this.currentRoute !== '/subscriptions') { // ✅ Prevent infinite loop
        this.router.navigate(['/subscriptions']);
      }
    }

    if (active) {
      this.lastCheckedStatus = true; // ✅ Reset if subscription is active
    }
  }

  /**
   * Get all subscriptions from the API.
   * If the data is already cached and forceRefresh is false, returns the cached data.
   *
   * @param forceRefresh - Set to true to bypass cache and reload data from the API.
   * @returns An observable containing the subscriptions.
   */
  public getAllSubscriptions(params: any = {}, forceRefresh: boolean = false): Observable<any> {
    // Generate cache key based on params (e.g., type=hrm&page=1)
    const cacheKey = this.generateCacheKey(params);

    if (!forceRefresh && this.subscriptionsCache[cacheKey]) {
      return of(this.subscriptionsCache[cacheKey]); // Return cached response if available
    }

    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key]);
      }
    });

    return this.http.get<any[]>(`${this.system.apiURL}/subscription/list`, { params: httpParams }).pipe(
      tap(subs => this.subscriptionsCache[cacheKey] = subs) // Cache response using key
    );
  }

  /**
   * Generates a unique cache key based on API parameters.
   */
  private generateCacheKey(params: any): string {
    return Object.keys(params)
      .filter(key => params[key] !== null && params[key] !== undefined)
      .map(key => `${key}=${params[key]}`)
      .join('&') || 'all';
  }
}
